import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {isEmpty, orderBy} from 'lodash'
import {Button, Col, OverlayTrigger, Row, Tooltip} from 'react-bootstrap'
import {Timeline as GenericTimeline} from '../../../../components/Timeline';
import {formatDateVanilla, formatDateWithTime, splitDateTime,} from '../../../../utils/util';
import NoteCard from './NoteCard';
import {useNotesContext} from "../context/Notes.context";
import {FilterGroupNew} from '../../../../components/filters/filterGroupNew';
import {AuthorLabel} from "../constants";
import {getFilterDef} from "../../../../components/filters/filterDefinition";
import {mapFiltersSelected} from "../mappers/FiltersMapper";
import {useAuth} from "../../../../context/authContext";
import AddNoteModal from "./AddNoteModal/AddNoteModal";

const componentName = 'note';

const getNotesRequestKey = (patientId, requestBody) => {
  const search = requestBody ?? {};
  return `${patientId}:${JSON.stringify(search)}`;
};

function NotesTimeline({patient, reachedBottom, setReachedBottom}) {
  const {
    getNotes,
    scrollNotes,
    notesList,
    isLoading,
    totalSize,
    hasMore,
    notesSearch,
    setInitialState,
    userNameListFilter,
    toggleShowAddNote,
    getNoteCategories
  } = useNotesContext();

  const auth = useAuth();
  const authorizedForWriteNotes = auth.hasAnyAuthority(
    ["AUTH_PD_NOTES_WRITE_ACCESS"]);

  const [filterParams, setFilterParams] = useState([]);
  const [filters, setFilters] = useState([]);
  const [openStates, setOpenStates] = useState([]);
  const [expandAll, setExpandAll] = useState(false);
  const patientId = patient?.id;
  const initialLoadKeyRef = useRef(null);
  const notesListLength = notesList?.length ?? 0;

  useEffect(() => {
    if (patientId) {
      const initialSearch = notesSearch ?? {};
      const requestKey = getNotesRequestKey(patientId, initialSearch);
      if (initialLoadKeyRef.current === requestKey) {
        return;
      }

      initialLoadKeyRef.current = requestKey;
      setInitialState();
      getNotes(patientId, initialSearch);
      getNoteCategories();
    }
    // NotesContext recreates callbacks and notesSearch as requests resolve.
    // The initial drawer load should only run when the selected patient changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId])

  useEffect(() => {
    if (notesListLength > 0 && reachedBottom && !isLoading && hasMore) {
      scrollNotes(patientId, notesSearch)
      .then(() => setReachedBottom(false))
    }
  }, [hasMore, isLoading, notesListLength, notesSearch, patientId, reachedBottom, scrollNotes, setReachedBottom])

  useEffect(() => {
    const filterNames = [
      'Note Category',
    ]

    userNameListFilter?.length > 0 && filterNames.push('Note Author')
    const userNameList = [...userNameListFilter];
    const newFilters = filterNames.map(f => {
      let filterProps = getFilterDef(f);
      if (f === 'Note Author') {
        filterProps = {
          ...filterProps,
          componentProps: {
            ...filterProps.componentProps,
            refreshDataOnStateChange: userNameListFilter,
            getData: () => Promise.resolve(
              orderBy(userNameList, [item => item.text.toLowerCase()],
                ['asc']))
          }
        }
      }
      return filterProps;
    });

    setFilters(newFilters);
  }, [userNameListFilter])

  const createdAtAlreadySeenSet = new Set();

  const millisecondsRemover = (createdAtFormatted) => {
    if (!createdAtFormatted) {
      return null;
    }

    const date = createdAtFormatted.split(":");
    date.pop();
    return date.join('-')
  }

  useEffect(() => {
    if (notesList) {
      setOpenStates(prevStates => {
        const newStates = new Array(notesList.length).fill(expandAll);
        prevStates.forEach((state, index) => {
          if (index < newStates.length) {
            newStates[index] = state;
          }
        });
        const hasChanged = newStates.length !== prevStates.length
          || newStates.some((state, index) => state !== prevStates[index]);

        return hasChanged ? newStates : prevStates;
      });
    }
  }, [expandAll, notesList])

  const selectedFilterParams = useMemo(
    () => mapFiltersSelected(filterParams),
    [filterParams]
  );

  const handleFilterChange = useCallback(params => {
    setReachedBottom(false)
    setFilterParams(params)
    getNotes(patientId, {...notesSearch, pageNumber: 1, filters: params})
  }, [getNotes, notesSearch, patientId, setReachedBottom])

  const toggleAll = (state) => {
    setExpandAll(state)
    setOpenStates(prev => prev.map(() => state));
  };

  const toggleIndividual = (index) => {
    setOpenStates(prev => prev.map((state, i) => i === index ? !state : state));
  };

  return (
    <>
      <AddNoteModal patient={patient}/>

      <Row className={componentName + "-notes-timeline-view"}>
        <Row className="stickyscroll bg-light no-padding-right">
          <Col md={6}>
            <FilterGroupNew
              filters={filters}
              handleChange={handleFilterChange}
              defaultFilterParams={[]}
              filterParams={selectedFilterParams}
              isManageVisit={true}
            />
          </Col>
          <Col md={6} style={{textAlign: 'end'}} className="no-padding-right">
            <Button disabled={!authorizedForWriteNotes} onClick={() => {
              toggleShowAddNote(true);
            }}>Add Note</Button>
          </Col>
          <Row className='align-items-baseline'>
            <Col md={6}>
              <span
                bg="light"
                id={`${componentName}-patientCount`}
                className="border text-dark badge badge-pill mt-2">
                {totalSize}{" "}{"Note"}{totalSize === 1 ? '' : 's'}
              </span>
            </Col>
            <Col md={6} className='text-end'>
              <OverlayTrigger
                placement="auto"
                overlay={
                  <Tooltip>
                    Expand / Collapse All
                  </Tooltip>
                }
              >
                <span
                  bg="light"
                  className="border text-dark badge badge-pill mt-2 font-sm"
                  onClick={() => toggleAll(!expandAll)}
                  style={{maxWidth: 'fit-content', marginRight: '-30px'}}
                >
                  <i className='fas fa-angle-double-up'/>
                  <i className='fas fa-angle-double-down'/>
                </span>
              </OverlayTrigger>
            </Col>
          </Row>
        </Row>
        <GenericTimeline
          items={notesList ?? []}
          titleBuilder={(note, idx) => {
            // by definition, notes with the same date (excluding milliseconds) must be grouped
            const createdAtWithoutMilliseconds = millisecondsRemover(
              formatDateWithTime(note.dateTime)
            );
            const shouldShowCreatedAt = !createdAtAlreadySeenSet.has(
              createdAtWithoutMilliseconds);

            if (shouldShowCreatedAt) {
              createdAtAlreadySeenSet.add(createdAtWithoutMilliseconds)
            }

            const [date, time] = splitDateTime(
              formatDateVanilla(note?.dateTime, "MMM dd, yyyy, hh:mm a"));

            return note.dateTime && shouldShowCreatedAt ? (
              <div>
              <span className="d-block fw-bold">
                {date}
              </span>
                <span className="d-block">
                {time}
              </span>
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="auto"
                  overlay={
                    <Tooltip>
                      {note.author ? note.author : "No Author"}
                    </Tooltip>
                  }>
                  <AuthorLabel
                    className="d-block badge badge-pill border-0 text-truncate">
                    {note.author ? note.author : "No Author"}
                  </AuthorLabel>
                </OverlayTrigger>
              </div>
            ) : (
              !isEmpty(createdAtWithoutMilliseconds) ? "" : "-"
            )
          }
          }
          bodyBuilder={(note, idx) => (
            <NoteCard
              section={'all'}
              idx={idx}
              note={note}
              toggleIndividual={() => toggleIndividual(idx)}
              open={openStates[idx]}
            />
          )}
        />
        {isLoading &&
          <div className="d-flex w-100 justify-content-center">
            <div className="spinner-border text-dark" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        }
        {
          !hasMore &&
          <div className="d-flex w-100 justify-content-center">
            <p>You have reached the bottom</p>
          </div>
        }
      </Row>
    </>
  )
}

export default NotesTimeline
